import React, { useEffect, useRef, useState } from 'react';
import { formatDateTime } from "../../helpers/formatDateTime";
import { listCustomers, getCustomer } from '../../services/customer/customer.service';
import type { CustomerListItem } from '../../services/customer/customer.types';
import { ModalContent } from '../ui/ModalContent';
import { StatusMessage } from '../ui';
import Modal from '../ui/Modal';
import { BuildingForm } from './BuildingForm';
import { createCustomer } from '../../services/customer/customer.service';
import type { CreateCustomerRequest } from '../../services/customer/customer.types';
import { getPendingKey } from '../../features/pending/pendingSites';
import type { PendingSite } from '../../features/pending/pendingSites';

type SiteSummary = PendingSite;

interface Props {
    selectedSite?: SiteSummary | null;
    selectedSites?: SiteSummary[];
    mode?: 'approve' | 'reject' | null;
    onApprove?: (s: SiteSummary, customerId: number) => Promise<void> | void;
    onReject?: (s: SiteSummary, customerId?: number | null, reason?: string) => Promise<void> | void;
    onBulkApprove?: (items: { s: SiteSummary; customerId: number }[]) => Promise<void> | void;
    onBulkReject?: (items: { s: SiteSummary; customerId?: number | null; reason?: string }[]) => Promise<void> | void;
    actionInFlight?: string | null;
    onRemoveSelected?: (key: string) => void;
    // When rendered inside a parent Modal, set this to true so the form will
    // directly call the provided callbacks on submit and avoid showing its own
    // inner confirm dialog. The outer Modal's OK button will trigger the submit.
    isInsideModal?: boolean;
    formRef?: React.RefObject<HTMLFormElement | null>;
    onCustomerDialogChange?: (open: boolean) => void;
}

const PendingSitesForm: React.FC<Props> = ({ selectedSite, selectedSites, mode, onApprove, onReject, onBulkApprove, onBulkReject, actionInFlight, onRemoveSelected, isInsideModal, formRef, onCustomerDialogChange }) => {
    const [customers, setCustomers] = useState<CustomerListItem[]>([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customersError, setCustomersError] = useState<string | null>(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItem | null>(null);
    const [reason, setReason] = useState<string>('');
    // bulk state: mapping key -> customerId or reason
    const [bulkSelections, setBulkSelections] = useState<Record<string, number | null>>({});
    const [bulkReasons, setBulkReasons] = useState<Record<string, string>>({});
    // create-customer modal state
    const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState<boolean>(false);
    const [creatingCustomer, setCreatingCustomer] = useState<boolean>(false);
    const [createCustomerError, setCreateCustomerError] = useState<string | null>(null);
    const [createCustomerSuccess, setCreateCustomerSuccess] = useState<string | null>(null);
    // when creating from a bulk entry, targetKey will be set so we assign the created customer to that bulk selection
    const [createCustomerTargetKey, setCreateCustomerTargetKey] = useState<string | null>(null);
    const createCustomerFormRef = useRef<HTMLDivElement>(null);

    //modal variables
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
    const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
    const resolverRef = useRef<((value: boolean) => void) | null>(null);
    useEffect(() => { onCustomerDialogChange?.(openCreateCustomerModal); }, [openCreateCustomerModal, onCustomerDialogChange]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoadingCustomers(true);
            setCustomersError(null);
            try {
                const res = await listCustomers();
                if (res?.success && Array.isArray(res.data)) {
                    if (!mounted) return;
                    setCustomers(res.data as CustomerListItem[]);
                } else {
                    if (mounted) setCustomersError(res?.error?.message || 'Failed to load customers');
                }
            } catch (err: any) {
                if (mounted) setCustomersError(err?.message || String(err));
            } finally {
                if (mounted) setLoadingCustomers(false);
            }
        };
        void load();
        return () => { mounted = false; };
    }, []);

    // when selectedCustomerId changes, fetch the full customer details
    useEffect(() => {
        if (!selectedCustomerId) {
            setSelectedCustomer(null);
            return;
        }
        let mounted = true;
        const load = async () => {
            try {
                const res = await getCustomer(selectedCustomerId);
                if (!mounted) return;
                if (res?.success && res.data) setSelectedCustomer(res.data as CustomerListItem);
                else setSelectedCustomer(null);
            } catch (err) {
                console.info('Failed to load customer details', err);
                if (mounted) setSelectedCustomer(null);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [selectedCustomerId]);

    // Preselect customer when the selected site already contains a customerId
    useEffect(() => {
        if (selectedSite?.customerId) setSelectedCustomerId(selectedSite.customerId);
        else setSelectedCustomerId(null);
        // reset reason when site or mode changes
        setReason('');
    }, [selectedSite]);

    // Initialize bulk selections when selectedSites changes
    useEffect(() => {
        if (!selectedSites || selectedSites.length === 0) {
            setBulkSelections({});
            setBulkReasons({});
            return;
        }
        setBulkSelections(previous => Object.fromEntries(selectedSites.map(site => {
            const key = getPendingKey(site);
            return [key, key in previous ? previous[key] : site.customerId ?? null];
        })));
        setBulkReasons(previous => Object.fromEntries(selectedSites.map(site => {
            const key = getPendingKey(site);
            return [key, previous[key] ?? ''];
        })));
    }, [selectedSites]);

    // Create-customer submit handler used by the modal BuildingForm (shared for bulk & single flows)
    const handleCreateCustomerSubmit = async (values: CreateCustomerRequest) => {
        if (creatingCustomer || actionInFlight) return;
        setCreateCustomerError(null);
        setCreateCustomerSuccess(null);
        setCreatingCustomer(true);
        try {
            const res = await createCustomer(values);
            if (res?.success && res.data) {
                const created = res.data as CustomerListItem;
                // add to customers list and select
                setCustomers(prev => {
                    // if already present, move to front
                    const exists = prev.find(c => c.id === created.id);
                    if (exists) return [created, ...prev.filter(c => c.id !== created.id)];
                    return [created, ...prev];
                });
                if (createCustomerTargetKey) {
                    setBulkSelections(prev => ({ ...prev, [createCustomerTargetKey]: created.id }));
                } else {
                    setSelectedCustomerId(created.id);
                }
                setCreateCustomerSuccess('Customer created');
                setOpenCreateCustomerModal(false);
                setCreateCustomerTargetKey(null);
            } else {
                setCreateCustomerError(res?.error?.message || 'Failed to create customer');
            }
        } catch (err: any) {
            setCreateCustomerError(err?.message || String(err));
        } finally {
            setCreatingCustomer(false);
        }
    };

    // If no site selected, show helper
    // Bulk selected view
    if ((!selectedSite) && (!selectedSites || selectedSites.length === 0)) {
        return (
            <div id="pending-sites-form" className="pending-sites-form">
              
            </div>
        );
    }

    // If a site is selected but no mode chosen, prompt the user to pick Approve or Reject from the actions
    if (!mode) {
        // show minimal prompt for single or multiple selection
        return (
            <div id="pending-sites-form" className="pending-sites-form alarm-content mt-2">
                <h3 className='heading heading__title--pending-sites'>Pending Site Selected</h3>
                <p>Choose Approve or Reject from the row actions or the bulk toolbar to open the appropriate form.</p>
                {selectedSite && (
                    <>
                        <div className="form-row">
                            <label>Identifier</label>
                            <div>{selectedSite.siteIdentifier}</div>
                        </div>
                        <div className="form-row">
                            <label>Site Name</label>
                            <div>{selectedSite.siteName}</div>
                        </div>
                    </>
                )}
                {!selectedSite && selectedSites && selectedSites.length > 0 && (
                    <div className="form-row">
                        <div>{selectedSites.length} sites selected — choose Approve or Reject to continue.</div>
                    </div>
                )}
            </div>
        );
    }

    // Bulk flow: if selectedSites is provided render multiple entries
    if (selectedSites && selectedSites.length > 0) {
        const approvingBulk = Boolean(selectedSites && actionInFlight);
        const submitBulk = async (e: React.FormEvent) => {
            e.preventDefault();
            if (actionInFlight || openCreateCustomerModal) return;
            // build items for approve or reject
            if (mode === 'approve') {
                const items: { s: SiteSummary; customerId: number }[] = [];
                for (const s of selectedSites) {
                    const key = s.pendingSiteRequestGuid ?? s.siteGuid ?? String(s.id);
                    const cid = bulkSelections[key];
                    if (!cid) {
                        setCustomersError('Please select a customer for all selected sites before approving.');
                        return;
                    }
                    items.push({ s, customerId: cid });
                }
                if (isInsideModal) {
                    setCustomersError(null);
                    await onBulkApprove?.(items);
                    return;
                }
                // fallback to existing confirm flow
                const confirmMessage = `Approve ${items.length} selected site(s)?`;
                setModalMessageText(confirmMessage);
                setModalHeadingText("Approve Sites");
                setOpenModalConfirm(true);
                const confirmed = await waitForModalConfirm();
                if (!confirmed) return;
                await onBulkApprove?.(items);
            } else {
                const items: { s: SiteSummary; customerId?: number | null; reason?: string }[] = [];
                for (const s of selectedSites) {
                    const key = s.pendingSiteRequestGuid ?? s.siteGuid ?? String(s.id);
                    const cid = bulkSelections[key] ?? null;
                    const r = (bulkReasons[key] ?? '').trim();
                    if (!r) {
                        setCustomersError('Please provide a reason for all rejected sites.');
                        return;
                    }
                    items.push({ s, customerId: cid, reason: r });
                }
                if (isInsideModal) {
                    setCustomersError(null);
                    await onBulkReject?.(items);
                    return;
                }
                const confirmMessage = `Reject ${items.length} selected site(s)?`;
                setModalMessageText(confirmMessage);
                setModalHeadingText("Reject Sites");
                setOpenModalConfirm(true);
                const confirmed = await waitForModalConfirm();
                if (!confirmed) return;
                await onBulkReject?.(items);
            }
        };

        const handleApproveAll = async () => {
            // legacy kept for callers expecting it; delegate to submitBulk without event
            await submitBulk(new Event('submit') as unknown as React.FormEvent);
        };

        const handleRejectAll = async () => {
            // delegate to the form submit handler so behavior is consistent
            await submitBulk(new Event('submit') as unknown as React.FormEvent);
        };

        const waitForModalConfirm = (): Promise<boolean> => {
            return new Promise((resolve) => {
                (resolverRef as React.MutableRefObject<((value: boolean) => void) | null>).current = resolve;
                setOpenModalConfirm(true);
            });
        };

        
        
        return (
            <>
                <Modal
                    isOpen={openModalConfirm}
                    title=""
                    content={<ModalContent
                        heading={modalHeadingText}
                        message={modalMessageText}/>}
                        onConfirm={() => {
                        resolverRef.current?.(true);
                        setOpenModalConfirm(false);
                    }}
                    onCancel={() => {
                        resolverRef.current?.(false);
                        setOpenModalConfirm(false);
                    }}
                />
                <Modal
                    isOpen={openCreateCustomerModal}
                    title="Create Customer"
                    content={
                        <div ref={createCustomerFormRef}>
                            {createCustomerError && <StatusMessage kind="error">{createCustomerError}</StatusMessage>}
                            {createCustomerSuccess && <StatusMessage kind="success">{createCustomerSuccess}</StatusMessage>}
                            <BuildingForm
                                key={'create-customer'}
                                onSubmit={handleCreateCustomerSubmit}
                                onCancel={() => { if (!creatingCustomer) { setOpenCreateCustomerModal(false); setCreateCustomerTargetKey(null); } }}
                                isLoading={creatingCustomer}
                            />
                        </div>
                    }
                    onConfirm={() => {
                        const form = createCustomerFormRef.current?.querySelector('form') as HTMLFormElement | null;
                        if (form && !creatingCustomer) { form.requestSubmit(); }
                    }}
                    okButtonLoading={creatingCustomer} cancelButtonLoading={creatingCustomer}
                    onCancel={() => { if (!creatingCustomer) { setOpenCreateCustomerModal(false); setCreateCustomerTargetKey(null); } }}
                />
                <form id="pending-sites-form" ref={formRef} onSubmit={submitBulk}>
                <fieldset disabled={!!actionInFlight || openCreateCustomerModal} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                <h3 className='heading heading__title--pending-sites'>Pending Site Details ({selectedSites.length})</h3>
                {customersError && <StatusMessage kind="error">{customersError}</StatusMessage>}

                <div className="pending-sites-form__bulk-actions">
                    {selectedSites.map(s => {
                        const key = s.pendingSiteRequestGuid ?? s.siteGuid ?? String(s.id);
                        return (
                            <div key={key} className="form-entry mb-2" style={{ borderBottom: '1px solid #eee' }}>
                                <div className="form-entry__heading">
                                    <h3 className='heading__title--building'>{s.siteName}</h3>
                                    <button
                                        type="button"
                                        className="btn__close se-icon--close"
                                        aria-label={`Remove ${s.siteName || s.siteIdentifier || s.id}`}
                                        onClick={() => {
                                            // remove local bulk state for this key
                                            setBulkSelections(prev => {
                                                const copy = { ...prev };
                                                delete copy[key];
                                                return copy;
                                            });
                                            setBulkReasons(prev => {
                                                const copy = { ...prev };
                                                delete copy[key];
                                                return copy;
                                            });
                                            onRemoveSelected?.(key);
                                        }}
                                    />
                                </div>
                                <div className="form-row">
                                    <label>Identifier</label>
                                    <div className="form-row__info">{s.siteIdentifier}</div>
                                </div>
                                <div className="form-row">
                                    <label>Region</label>
                                    <div className="form-row__info">{s.region}</div>
                                </div>
                                <div className="form-row">
                                    <label>Created At</label>
                                    <div className="form-row__info">{formatDateTime(s.createdAt)}</div>
                                </div>
                                <div className="form-row">
                                    <label>System Type</label>
                                    <div className="form-row__info">{s.systemTypeName}</div>
                                </div>
                                {mode !== 'reject' && (
                                    <>
                                        <div className='form-row__select'>
                                            <label htmlFor={`customer-${key}`} className='mb-2'>Select Existing Customer</label>
                                            {!loadingCustomers && Array.isArray(customers) && customers.length > 0 && (
                                                <select
                                                    id={`customer-${key}`}
                                                    className="form__control mt-1"
                                                    value={bulkSelections[key] ?? ''}
                                                    onChange={e => setBulkSelections(prev => ({ ...prev, [key]: e.target.value ? Number(e.target.value) : null }))}
                                                >
                                                    <option value="">-- select customer --</option>
                                                    {customers.map(c => (
                                                        <option key={c.id} value={c.id}>{c.customerName ?? c.id}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <div className='form-row'>
                                            <button
                                                type="button"
                                                className='btn btn-add-new'
                                                onClick={() => {
                                                    setCreateCustomerTargetKey(key);
                                                    setCreateCustomerError(null);
                                                    setCreateCustomerSuccess(null);
                                                    setOpenCreateCustomerModal(true);
                                                }}
                                            >Add New Customer</button>
                                        </div>
                                    </>
                                )}
                                {mode === 'reject' && (
                                    <div className="form-row__select mb-1">
                                        <label htmlFor={`reason-${key}`} className='mb-2'>Reason (reject only)</label>
                                        <textarea
                                            id={`reason-${key}`}
                                            className="form__control mt-1"
                                            rows={2}
                                            value={bulkReasons[key] ?? ''}
                                            onChange={e => setBulkReasons(prev => ({ ...prev, [key]: e.target.value }))}
                                            // For reject mode the reason must be provided even when customer is not selected
                                            disabled={mode !== 'reject' && !bulkSelections[key]}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                </fieldset>
                </form>
            </>
        );
    }

    // Single-site flow: render details for the selectedSite when provided
    if (selectedSite) {
        const waitForModalConfirm = (): Promise<boolean> => {
            return new Promise(resolve => {
                (resolverRef as React.MutableRefObject<((value: boolean) => void) | null>).current = resolve;
                setOpenModalConfirm(true);
            });
        };
        const key = selectedSite.pendingSiteRequestGuid ?? selectedSite.siteGuid ?? String(selectedSite.id);
        const submitSingle = async (e: React.FormEvent) => {
            e.preventDefault();
            if (actionInFlight || openCreateCustomerModal) return;
            if (mode === 'approve' && !selectedCustomerId) {
                setCustomersError('Please select a customer before approving.');
                return;
            }
            if (mode === 'reject' && !reason.trim()) {
                setCustomersError('Please provide a reason for rejected sites.');
                return;
            }
            setCustomersError(null);

            if (isInsideModal) {
                if (mode === 'approve' && selectedCustomerId) await onApprove?.(selectedSite, selectedCustomerId);
                else await onReject?.(selectedSite, selectedCustomerId, reason.trim());
                return;
            }

            // existing confirm flow when not inside an outer modal
            if (mode === 'approve') {
                setModalMessageText('Are you sure you want to approve this pending site for the selected customer?');
                setModalHeadingText("Approve Customer");
                setOpenModalConfirm(true);
                const confirmed = await waitForModalConfirm();
                if (!confirmed) return;
                if (selectedCustomerId) await onApprove?.(selectedSite, selectedCustomerId);
            } else {
                setModalMessageText('Are you sure you want to reject this pending site for the selected customer?');
                setModalHeadingText("Reject Sites");
                setOpenModalConfirm(true);
                const confirmed = await waitForModalConfirm();
                if (!confirmed) return;
                await onReject?.(selectedSite, selectedCustomerId, reason.trim());
            }
        };

        return (
            <>
                <Modal
                    isOpen={openModalConfirm}
                    title=""
                    content={<ModalContent
                        heading={modalHeadingText}
                        message={modalMessageText}/>}
                    onConfirm={() => {
                        resolverRef.current?.(true);
                        setOpenModalConfirm(false);
                    }}
                    onCancel={() => {
                        resolverRef.current?.(false);
                        setOpenModalConfirm(false);
                    }}
                />
                <Modal
                    isOpen={openCreateCustomerModal}
                    title="Create Customer"
                    content={
                        <div ref={createCustomerFormRef}>
                            {createCustomerError && <StatusMessage kind="error">{createCustomerError}</StatusMessage>}
                            {createCustomerSuccess && <StatusMessage kind="success">{createCustomerSuccess}</StatusMessage>}
                            <BuildingForm
                                key={'create-customer-single'}
                                onSubmit={handleCreateCustomerSubmit}
                                onCancel={() => { if (!creatingCustomer) { setOpenCreateCustomerModal(false); setCreateCustomerTargetKey(null); } }}
                                isLoading={creatingCustomer}
                            />
                        </div>
                    }
                    onConfirm={() => {
                        const form = createCustomerFormRef.current?.querySelector('form') as HTMLFormElement | null;
                        if (form && !creatingCustomer) { form.requestSubmit(); }
                    }}
                    okButtonLoading={creatingCustomer} cancelButtonLoading={creatingCustomer}
                    onCancel={() => { if (!creatingCustomer) { setOpenCreateCustomerModal(false); setCreateCustomerTargetKey(null); } }}
                />
                <form id="pending-sites-form" ref={formRef} onSubmit={submitSingle}>
                <fieldset disabled={!!actionInFlight || openCreateCustomerModal} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                <h3 className='heading heading__title--pending-sites'>Pending Site Details</h3>
                {mode === 'reject' && customersError && <StatusMessage kind="error">{customersError}</StatusMessage>}
                <div className="form-entry" >
                    <div className="form-entry__heading">
                        <h3 className='heading__title--building'>{selectedSite.siteName}</h3>
                        <button
                            type="button"
                            className="btn__close se-icon--close"
                            aria-label={`Remove ${selectedSite.siteName || selectedSite.siteIdentifier || selectedSite.id}`}
                            onClick={() => {
                                // remove local bulk state for this key and notify parent so row is deselected
                                setBulkSelections(prev => {
                                    const copy = { ...prev };
                                    delete copy[key];
                                    return copy;
                                });
                                setBulkReasons(prev => {
                                    const copy = { ...prev };
                                    delete copy[key];
                                    return copy;
                                });
                                onRemoveSelected?.(key);
                            }}
                        />
                    </div>
                    <div className="form-row">
                        <label>Identifier</label>
                        <div className="form-row__info">{selectedSite.siteIdentifier}</div>
                    </div>
                    <div className="form-row">
                        <label>Site Name</label>
                        <div className="form-row__info">{selectedSite.siteName}</div>
                    </div>
                    <div className="form-row">
                        <label>Region</label>
                        <div className="form-row__info">{selectedSite.region}</div>
                    </div>
                    <div className="form-row">
                        <label>System Type</label>
                        <div className="form-row__info">{selectedSite.systemTypeName}</div>
                    </div>
                    <div className="form-row">
                        <label>Created</label>
                        <div className="form-row__info">{formatDateTime(selectedSite.createdAt)}</div>
                    </div>

                    {mode !== 'reject' && (
                        <>
                            <div className='form-row__select'>
                                <label htmlFor="customerSelect" className='mb-2'>Select Existing Customer</label>
                                {loadingCustomers && <div>Loading customers…</div>}
                                {customersError && <StatusMessage kind="error">{customersError}</StatusMessage>}
                                {!loadingCustomers && Array.isArray(customers) && customers.length > 0 && (
                                    <select id="customerSelect" className="form__control mt-1" value={selectedCustomerId ?? ''} onChange={e => setSelectedCustomerId(e.target.value ? Number(e.target.value) : null)}>
                                        <option value="">-- select customer --</option>
                                        {customers.map(c => (
                                            <option key={c.id} value={c.id}>{c.customerName ?? c.id}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className='form-row mb-1'>
                                <button
                                    type="button"
                                    className='btn btn-add-new'
                                    onClick={e => {
                                        e.preventDefault();
                                        setCreateCustomerTargetKey(null);
                                        setCreateCustomerError(null);
                                        setCreateCustomerSuccess(null);
                                        setOpenCreateCustomerModal(true);
                                    }}
                                >Add New Customer</button>
                            </div>
                        </>
                    )}

                    {mode === 'reject' && (
                        <div className="form-row__select mb-1">
                            <label htmlFor={`reason-${key}`} className='mb-2'>Reason (reject only)</label>
                            <textarea id={`reason-${key}`} className="form__control" rows={3} value={reason} onChange={e => setReason(e.target.value)} disabled={mode !== 'reject' && !selectedCustomerId} />
                        </div>
                    )}

                    </div>
                </fieldset>
                </form>
            </>
        );
    }
};

export default PendingSitesForm;